import { orderTypes } from "redux/reducers/orders/orderTypes";
import axios from "axios";
import { api_get, api_post, api_put, api_delete, PROYECT } from "services/api";
import {
  setSnackbar,
  startRequestComp,
  stopRequestComp,
} from "../search/searchActions";
import { extractMessage } from "helpers/extractError";
import { validateObject } from "helpers/validateObject";
import { formatNumber } from "helpers/getNumberFormats";
import { isNetworkError } from "helpers/isNetworkError";

const {
  SET_FILTERS_PARAMS,
  GET_ORDERS,
  SELECT_ORDER,
  FILTER_ORDERS,
  SET_ORDER_CAUSES,
  EDIT_ORDER,
  ADD_COMMENT,
  GET_SERVICE_DETAILS,
  SET_NEW_PAYMENT_LINK,
  ADD_ETA,
} = orderTypes;

const mainUrl = `${process.env.REACT_APP_URL}${PROYECT}`;

const URL_TOKEN = "https://app.rapidcanvas.ai/api/access_key/token";
const URL_PREDICT =
  "https://app.rapidcanvas.ai/api/v2/predict/eta_services_api";
const API_KEY = process.env.REACT_APP_RAPID_CANVAS_API_KEY || "api-key";

export const ordersHandleFiltersInputs = (field, data) => {
  const newPayload = {
    [field]: data,
  };
  return {
    type: SET_FILTERS_PARAMS,
    payload: newPayload,
  };
};

export const fetchServiceDetails = (id) => {
  return async dispatch => {
    try {
      dispatch(startRequestComp('fetchServiceDetails'));

      const data = await api_get(`${mainUrl}/services/${id}`);
      dispatch({ type: GET_SERVICE_DETAILS, payload: data });
    } catch (error) {
      dispatch(setSnackbar(error))
    } finally {
      dispatch(stopRequestComp('fetchServiceDetails'))
    }
  };
}

/**
 * Sube una o varias imagenes de evidencia a un servicio.
 * POST /crm/services/:id/evidences  (multipart/form-data)
 * @param {string} id - ID del servicio
 * @param {FormData} formData - FormData con `type` y uno o varios `images`
 */
export const uploadServiceEvidence = (id, formData) => {
  return async dispatch => {
    try {
      dispatch(startRequestComp('loadingServiceEvidence'));

      await api_post(`${mainUrl}/services/${id}/evidences`, formData);

      await dispatch(fetchServiceDetails(id));
      dispatch(setSnackbar('Evidencia subida correctamente', 'success'));
    } catch (error) {
      dispatch(setSnackbar(error));
    } finally {
      dispatch(stopRequestComp('loadingServiceEvidence'));
    }
  };
}

/**
 * Elimina una imagen de evidencia de un servicio.
 * DELETE /crm/services/:id/evidences/:idImage
 * @param {string} id - ID del servicio
 * @param {string} idImage - ID de la imagen a eliminar
 * @param {Function} [onFinish] - Callback que se ejecuta al terminar (exito o error)
 */
export const deleteServiceEvidence = (id, idImage, onFinish = null) => {
  return async dispatch => {
    try {
      dispatch(startRequestComp('loadingServiceEvidence'));

      await api_delete(`${mainUrl}/services/${id}/evidences/${idImage}`);

      await dispatch(fetchServiceDetails(id));
      dispatch(setSnackbar('Evidencia eliminada', 'success'));
    } catch (error) {
      dispatch(setSnackbar(error));
    } finally {
      dispatch(stopRequestComp('loadingServiceEvidence'));
      onFinish && onFinish();
    }
  };
}

export const newPaymentLink = (paymentData) => {
  return async dispatch => {
    try {
      dispatch(startRequestComp('newPaymentLink'));

      const data = await api_post(`${mainUrl}/payments`, paymentData);
      dispatch({ type: SET_NEW_PAYMENT_LINK, payload: data });
    } catch (error) {
      dispatch(setSnackbar(error))
    } finally {
      dispatch(stopRequestComp('newPaymentLink'))
    }
  };
}

export const restoreOrderFilters = (params) => ({
  type: SET_FILTERS_PARAMS,
  payload: params,
});

export const getOrders = ({startDate, endDate, stations = [], isAgent = false}) => {
  const url = `${mainUrl}/services?`;
  return async dispatch => {
    if (typeof stations === 'string') {
      dispatch(setSnackbar('Por favor, selecciona al menos una estación valida'));
      return;
    }

    try {
      dispatch(startRequestComp("loadingOrders"));

      const stationsParams = stations
        .map((station) =>
          isAgent ? `agent=${station}` : `stations=${station}`,
        )
        .join("&");

      const dates = `start=${startDate}&end=${endDate}`;
      const ordersUrl = `${url}${dates}${
        stationsParams ? `&${stationsParams}` : ""
      }`;

      const data = await api_get(ordersUrl);
      dispatch({ type: GET_ORDERS, payload: data });
    } catch (error) {
      dispatch(setSnackbar(extractMessage(error)));
    }
    dispatch(stopRequestComp("loadingOrders"));
  };
};

export const getOrderCauses = (idStation) => {
  const url = `${mainUrl}/stations/causes?stations=${idStation}`;
  return async (dispatch) => {
    try {
      const resp = await api_get(url);
      dispatch({
        type: SET_ORDER_CAUSES,
        payload: resp,
      });
    } catch (err) {
      dispatch(setSnackbar(err));
    }
  };
};

export const editOrder = (idOrder, form, nav = null, blockSnackbar = false) => {
  const url = `${mainUrl}/services/${idOrder}`;
  return async (dispatch) => {
    try {
      const resp = await api_put(url, form);
      // resp[0] es la orden actualizada del backend. `_formIsAsap` es un
      // fallback transitorio (prefijo `_`): el reducer lo usa solo si la
      // respuesta del backend no trae `serviceComplement.isAsap`, y no se
      // persiste en el estado.
      const response = {
        ...resp[0],
        idUnit: form.unit,
        _formIsAsap: form.isAsap,
      };

      dispatch({
        type: EDIT_ORDER,
        payload: response,
      });

      if (!blockSnackbar) {
        dispatch(setSnackbar("Orden editada", "success"));
      }

      nav && nav();
    } catch (err) {
      dispatch(setSnackbar(err));
    }
  };
};

export const addComment = (idOrder, comment, clean = null) => {
  const url = `${mainUrl}/services/${idOrder}/annotations`
  return async dispatch => {
    dispatch(startRequestComp('loadingAddComment'))

    try {
      const resp = await api_post(url, comment)

      dispatch({
        type: ADD_COMMENT,
        payload: resp
      })

      clean && clean()
    } catch (err) {
      dispatch(setSnackbar(err));
    } finally {
      setTimeout(() => {
        dispatch(stopRequestComp("loadingAddComment"));
      }, 2000);
    }
  };
};

export const selectOrder = (order) => {
  return { type: SELECT_ORDER, payload: order };
};

export const filtersOrders = (timing) => {
  const upper = timing.toUpperCase();
  return { type: FILTER_ORDERS, payload: upper };
};

export const getOrderEta = (payload) => {
  return async (dispatch) => {
    let resp;
    try {
      let token = localStorage.getItem("eta_token");
      if (!token) {
        const responseToken = await axios.get(URL_TOKEN, {
          headers: {
            accept: "application/json",
            "X-API-KEY": API_KEY,
          },
        });
        token = responseToken.data.token;
        localStorage.setItem("eta_token", token);
      }

      const response = await axios.post(URL_PREDICT, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        timeout: 30000,
      });
      resp = response.data;
    } catch (error) {
      if (error.response && error.response.status === 401) {
        localStorage.removeItem("eta_token");
        dispatch(
          setSnackbar(
            "No se pudo obtener el ETA: sesión expirada. Intenta de nuevo.",
          ),
        );
      } else if (isNetworkError(error)) {
        dispatch(
          setSnackbar(
            "No se pudo conectar con RapidCanvas para obtener el ETA (error de red/CORS). Contacta al administrador.",
          ),
        );
      } else {
        dispatch(setSnackbar(extractMessage(error)));
      }
      return;
    }

    let eta = "N/A";
    try {
      const parsed = JSON.parse(resp);
      eta = parsed;
    } catch (error) {
      eta = resp;
    }

    dispatch({
      type: ADD_ETA,
      payload: {
        eta: validateObject(eta?.eta_from_now_max)
          ? formatNumber({ input: eta?.eta_from_now_max[0] })
          : "N/A",
      },
    });
  };
};
